import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { X, ArrowRight, Robot, Trophy, ChartLineUp, Sparkle } from "@phosphor-icons/react";

const STEPS = [
  {
    Icon: Robot,
    title: "Deploy your first bot",
    body: "Pick a symbol (say AAPL), choose a strategy (RSI is beginner-friendly), and tune two numbers. Your bot evaluates every 2 minutes on daily bars.",
    cta: "Take me to the bot builder",
    action: "goto:/bots",
  },
  {
    Icon: ChartLineUp,
    title: "Compare before you commit",
    body: "Backtest every bot against the last year of price data — you'll know what you're deploying before a single paper dollar is at stake. Bots have a Compare mode too.",
    cta: "Got it — next",
    action: "next",
  },
  {
    Icon: Trophy,
    title: "Learn from the community",
    body: "Head to the Leaderboard, copy a top public bot, and tweak its parameters. STRATOS will ping the original owner when your version starts winning.",
    cta: "Finish tour → Leaderboard",
    action: "goto:/leaderboard",
  },
];

export default function OnboardingTour() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  // Only show for authed users who haven't seen it yet
  if (!user || user.tour_seen || dismissed) return null;

  const markSeen = async () => {
    setDismissed(true);
    try {
      await api.post("/me/tour-seen");
      if (setUser) setUser({ ...user, tour_seen: true });
    } catch {
      // silent — a stale tour on next visit is OK
    }
  };

  const advance = async (action) => {
    if (action === "next") {
      setStep(step + 1);
      return;
    }
    if (action.startsWith("goto:")) {
      await markSeen();
      nav(action.slice(5));
    }
  };

  const s = STEPS[step];
  const Icon = s.Icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div
      data-testid="onboarding-tour"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <div
        className="w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-sm shadow-2xl relative"
        data-testid={`onboarding-step-${step + 1}`}
      >
        {/* Progress dots */}
        <div className="flex items-center justify-between px-5 pt-4">
          <div className="mono text-[10px] text-[#00C805] uppercase tracking-widest flex items-center gap-2">
            <Sparkle size={12} weight="fill" /> Welcome to STRATOS
          </div>
          <button
            onClick={markSeen}
            data-testid="onboarding-skip-btn"
            className="text-zinc-500 hover:text-white p-1"
            aria-label="Skip tour"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-5 pt-4 pb-2 flex items-center gap-2">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full flex-1 transition-colors ${
                i <= step ? "bg-[#00C805]" : "bg-zinc-700"
              }`}
            />
          ))}
        </div>

        <div className="p-5">
          <div className="p-3 bg-[#00C805]/10 rounded-sm inline-flex">
            <Icon size={20} weight="fill" className="text-[#00C805]" />
          </div>
          <h2 className="heading text-2xl font-black tracking-tighter mt-4">{s.title}</h2>
          <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{s.body}</p>
        </div>

        <div className="px-5 pb-5 flex items-center justify-between">
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
            Step {step + 1} / {STEPS.length}
          </div>
          <button
            onClick={() => advance(s.action)}
            data-testid="onboarding-next-btn"
            className="inline-flex items-center gap-1.5 text-sm text-black bg-[#00C805] hover:bg-[#00b004] rounded-sm px-4 py-2 mono text-[10px] uppercase tracking-widest font-semibold"
          >
            {s.cta} <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
